/**
 * grid-swatch-static — a drop-in replacement for Broadcast's core <grid-swatch> that
 * renders the collection color swatches WITHOUT the per-card `GET /products/{handle}.js`
 * fetch.
 *
 * Why: the core <grid-swatch> fetches each product's JSON on connect ONLY to confirm the
 * colour option exists before wiring hover behaviour. But the swatch buttons, variant
 * names, variant images and count are all already server-rendered by
 * product-grid-item.liquid, and Liquid only renders this element when the colour option
 * exists (`has_colors`). So the fetch is redundant — and at ~1 request per card it was the
 * request volume tripping Shopify's Cloudflare bot challenge during infinite scroll (see
 * ticket TAHARI-ISSUE-12551885871 investigation). This element mirrors grid-swatch's
 * init / hover / variant-image-swap logic verbatim and simply skips the fetch, so it
 * inits directly on connect.
 *
 * Toggle: product-grid-item.liquid renders <grid-swatch-static> instead of <grid-swatch>
 * when `settings.disable_swatch_fetch` is on. Turn that setting OFF to instantly revert to
 * the stock (fetching) core component.
 *
 * Scope: does NOT affect quick-add (separate `quick-add-product` component + its own
 * `?section_id=api-product-upsell` render) or siblings (`product-item-siblings`, rendered
 * entirely from Liquid data attributes) — both verified independent of grid-swatch.
 */
if (!customElements.get('grid-swatch-static')) {
  const selectors = {
    gridSwatchFieldset: '[data-grid-swatch-fieldset]',
    productItem: '[data-grid-item]',
    productInfo: '[data-product-information]',
    productImage: '[data-product-image]',
    swatchButton: '[data-swatch-button]',
  };

  const classes = {
    visible: 'is-visible',
    stopEvents: 'no-events',
  };

  const attributes = {
    swatchCount: 'data-swatch-count',
    variantName: 'data-swatch-variant-name',
    variantTitle: 'data-variant-title',
  };

  class GridSwatchStatic extends HTMLElement {
    constructor() {
      super();
      this.productItemMouseLeaveEvent = () => this.hideVariantImages();
      this.showVariantImageEvent = (swatchButton) => this.showVariantImage(swatchButton);
      this.resizeEvent = () => this.init();
    }

    connectedCallback() {
      this.productItem = this.closest(selectors.productItem);
      this.productInfo = this.closest(selectors.productInfo);
      if (!this.productItem || !this.productInfo) return;
      this.productImage = this.productItem.querySelector(selectors.productImage);
      this.swatchesStyle = (window.theme && theme.settings && theme.settings.collectionSwatchStyle) || '';
      document.addEventListener('theme:resize:width', this.resizeEvent);

      // No fetch: Liquid renders this element only when the colour option exists, so the
      // core's product-JSON existence check is unnecessary — init directly.
      this.init();
    }

    init() {
      this.swatchCount = this.productInfo.querySelector(`[${attributes.swatchCount}]`);
      this.swatchFieldset = this.productInfo.querySelector(selectors.gridSwatchFieldset);
      this.hideSwatchesTimer = 0;

      if (this.swatchCount && this.swatchCount.hasAttribute(attributes.swatchCount)) {
        if (this.swatchesStyle == 'text-slider') {
          this.swatchCount.addEventListener('mouseenter', () => {
            if (this.hideSwatchesTimer) clearTimeout(this.hideSwatchesTimer);

            this.productInfo.classList.add(classes.stopEvents);
            this.swatchFieldset.classList.add(classes.visible);
          });

          // Prevent color swatches blinking on mouse move
          this.productInfo.addEventListener('mouseleave', () => {
            this.hideSwatchesTimer = setTimeout(() => {
              this.productInfo.classList.remove(classes.stopEvents);
              this.swatchFieldset.classList.remove(classes.visible);
            }, 100);
          });
        }

        if (this.swatchesStyle == 'slider' || this.swatchesStyle == 'grid' || this.swatchesStyle == 'limited') {
          this.swatchFieldset.classList.add(classes.visible);
        }
      }

      this.bindSwatchButtonEvents();
    }

    bindSwatchButtonEvents() {
      this.querySelectorAll(selectors.swatchButton)?.forEach((swatchButton) => {
        // Show variant image when hovering a colour swatch
        swatchButton.addEventListener('mouseenter', this.showVariantImageEvent);
      });

      this.productItem.addEventListener('mouseleave', this.productItemMouseLeaveEvent);
    }

    showVariantImage(event) {
      const swatchButton = event.target;
      const variantName = swatchButton.getAttribute(attributes.variantName)?.replaceAll('"', "'");
      const variantImages = this.productImage.querySelectorAll(`[${attributes.variantTitle}]`);
      const variantImageSelected = this.productImage.querySelector(`[${attributes.variantTitle}="${variantName}"]`);

      // Hide all variant images
      variantImages?.forEach((image) => {
        image.classList.remove(classes.visible);
      });

      // Show selected variant image
      variantImageSelected?.classList.add(classes.visible);
    }

    hideVariantImages() {
      this.productImage.querySelectorAll(`[${attributes.variantTitle}].${classes.visible}`)?.forEach((image) => {
        image.classList.remove(classes.visible);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('theme:resize:width', this.resizeEvent);
    }
  }

  customElements.define('grid-swatch-static', GridSwatchStatic);
}
